import React, { useState, useEffect } from 'react';
import { Ticket, Copy, Check, Gift, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import api from '../lib/api';

export default function CodesPage() {
    const { t } = useLanguage();
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        loadCodes();
    }, []);

    const loadCodes = async () => {
        try {
            const response = await api.get('/codes', { params: { active_only: true } });
            setCodes(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Failed to load codes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async (code, id) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    const filteredCodes = codes.filter(code =>
        code.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.reward?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="codes-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/10">
                        <Ticket className="text-emerald-400" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold font-['Rajdhani'] tracking-wider text-white dark:text-white light:text-gray-900">
                            {t('codes')}
                        </h1>
                        <p className="text-gray-400 dark:text-gray-400 light:text-gray-600 text-sm sm:text-base">
                            {t('codesDescription')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                    type="text"
                    placeholder={t('codesSearch')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-11 bg-black/40 border-white/10 dark:bg-black/40 dark:border-white/10 light:bg-white light:border-gray-200"
                    data-testid="codes-search"
                />
            </div>

            {/* Codes List */}
            {loading ? (
                <div className="text-center py-16 text-gray-500">{t('loading')}</div>
            ) : filteredCodes.length === 0 ? (
                <div className="text-center py-16">
                    <Ticket size={48} className="mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400">{t('codesNone')}</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredCodes.map((entry) => (
                        <div
                            key={entry.id}
                            data-testid={`code-entry-${entry.id}`}
                            className={cn(
                                "group relative overflow-hidden rounded-xl",
                                "bg-card dark:bg-card light:bg-white",
                                "border border-white/5 dark:border-white/5 light:border-gray-200",
                                "hover:border-emerald-500/30 transition-all duration-200"
                            )}
                        >
                            <div className="flex flex-col sm:flex-row items-stretch">
                                {/* Left: Code + Copy Button */}
                                <div className="flex items-center gap-3 p-4 sm:p-5 sm:min-w-[320px] sm:border-r border-b sm:border-b-0 border-white/5 dark:border-white/5 light:border-gray-200">
                                    <button
                                        onClick={() => handleCopy(entry.code, entry.id)}
                                        data-testid={`copy-code-${entry.id}`}
                                        className={cn(
                                            "shrink-0 p-2.5 rounded-lg border transition-all duration-200",
                                            copiedId === entry.id
                                                ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400"
                                                : "bg-white/5 border-white/10 text-gray-400 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/10"
                                        )}
                                        title={t('codesCopy')}
                                    >
                                        {copiedId === entry.id ? (
                                            <Check size={18} />
                                        ) : (
                                            <Copy size={18} />
                                        )}
                                    </button>
                                    <div className="min-w-0">
                                        <span className="font-mono text-lg font-bold text-white dark:text-white light:text-gray-900 tracking-wide">
                                            {entry.code}
                                        </span>
                                        {copiedId === entry.id && (
                                            <span className="block text-xs text-emerald-400 mt-0.5">
                                                {t('codesCopied')}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Right: Reward / Description */}
                                <div className="flex-1 p-4 sm:p-5 flex items-center gap-3">
                                    <Gift size={18} className="text-emerald-400 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-white dark:text-white light:text-gray-900 font-medium">
                                            {entry.reward}
                                        </p>
                                        {entry.description && (
                                            <p className="text-sm text-gray-500 mt-0.5 truncate">
                                                {entry.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
